import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { TASK_STATUSES, PRIORITIES } from "@/lib/taskman";

const empty = {
  title: "", description: "", status: "todo", priority: "normal",
  execution_mode: "parallel", assignee_key: "", start_date: "",
  due_date: "", estimate_hours: "", actual_hours: "",
};

export default function TaskDialog({
  open, onOpenChange, initial, onSave, assignees,
  // Optional destination pickers (Task Manager "new task" flow)
  destination, clients = [], projects = [], proposals = [],
}) {
  const [form, setForm] = useState(empty);
  const [dest, setDest] = useState({ client_id: "", project_id: "", proposal_id: "" });
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.task_id);

  useEffect(() => {
    if (!open) return;
    if (initial?.task_id) {
      const a = initial.assignee;
      setForm({
        title: initial.title || "", description: initial.description || "",
        status: initial.status || "todo", priority: initial.priority || "normal",
        execution_mode: initial.execution_mode || "parallel",
        assignee_key: a ? `${a.type}:${a.id}:${a.name}` : "",
        start_date: initial.start_date || "", due_date: initial.due_date || "",
        estimate_hours: initial.estimate_hours ?? "",
        actual_hours: initial.actual_hours ?? "",
      });
    } else {
      setForm(empty);
      setDest({ client_id: "", project_id: "", proposal_id: "" });
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const [aType, aId, ...aName] = form.assignee_key ? form.assignee_key.split(":") : ["", ""];
    const payload = {
      title: form.title.trim(), description: form.description,
      status: form.status, priority: form.priority,
      execution_mode: form.execution_mode,
      assignee_type: aType || null, assignee_id: aId || null,
      assignee_name: aName.join(":") || null,
      start_date: form.start_date || null, due_date: form.due_date || null,
      estimate_hours: form.estimate_hours === "" ? null : Number(form.estimate_hours),
      actual_hours: form.actual_hours === "" ? null : Number(form.actual_hours),
    };
    if (destination && !isEdit) {
      payload.client_id = dest.client_id || null;
      if (dest.proposal_id) payload.proposal_id = dest.proposal_id;
      else payload.project_id = dest.project_id || null;
    }
    try {
      await onSave(payload);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const destProjects = projects.filter((p) => !dest.client_id || p.client_id === dest.client_id);
  const destProposals = proposals.filter(
    (p) => dest.client_id && p.client_id === dest.client_id && p.status !== "accepted");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit task" : "New task"}</DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit ? "Update the task details below." : "Tasks without a project land in the client's Do & Charge project."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {destination && !isEdit && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Client</Label>
                  <Select value={dest.client_id}
                    onValueChange={(v) => setDest({ client_id: v, project_id: "", proposal_id: "" })}>
                    <SelectTrigger data-testid="task-dest-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Project (optional)</Label>
                  <Select value={dest.project_id}
                    onValueChange={(v) => setDest((d) => ({ ...d, project_id: v, proposal_id: "" }))}>
                    <SelectTrigger data-testid="task-dest-project"><SelectValue placeholder="Do & Charge (default)" /></SelectTrigger>
                    <SelectContent>
                      {destProjects.map((p) => (
                        <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {destProposals.length > 0 && (
                <div>
                  <Label className="text-xs">Or attach to a proposal as a pending / forecast task</Label>
                  <Select value={dest.proposal_id}
                    onValueChange={(v) => setDest((d) => ({ ...d, proposal_id: v, project_id: "" }))}>
                    <SelectTrigger data-testid="task-dest-proposal"><SelectValue placeholder="No proposal" /></SelectTrigger>
                    <SelectContent>
                      {destProposals.map((p) => (
                        <SelectItem key={p.proposal_id} value={p.proposal_id}>{p.title || "Untitled proposal"} ({p.status})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-zinc-400 mt-1">
                    Pending tasks go live automatically when the proposal is approved.
                  </p>
                </div>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="What needs doing?" data-testid="task-title-input" autoFocus />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={3} value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="task-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Assignee</Label>
            <Select value={form.assignee_key} onValueChange={(v) => set("assignee_key", v)}>
              <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {(assignees?.team?.length > 0) && (
                  <SelectGroup>
                    <SelectLabel>Team</SelectLabel>
                    {assignees.team.map((m) => (
                      <SelectItem key={m.id} value={`team:${m.id}:${m.name}`}>{m.name}{m.title ? ` — ${m.title}` : ""}</SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {(assignees?.client_users?.length > 0) && (
                  <SelectGroup>
                    <SelectLabel>Client users</SelectLabel>
                    {assignees.client_users.map((u) => (
                      <SelectItem key={u.id} value={`client_user:${u.id}:${u.name}`}>{u.name}</SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start date</Label>
              <Input type="date" value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                data-testid="task-start-date" />
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <Input type="date" value={form.due_date}
                onChange={(e) => set("due_date", e.target.value)}
                data-testid="task-due-date" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Est. hours</Label>
              <Input type="number" min="0" step="0.25" value={form.estimate_hours}
                onChange={(e) => set("estimate_hours", e.target.value)}
                data-testid="task-estimate-hours" />
            </div>
            <div>
              <Label className="text-xs">Actual hours</Label>
              <Input type="number" min="0" step="0.25" value={form.actual_hours}
                onChange={(e) => set("actual_hours", e.target.value)}
                data-testid="task-actual-hours" />
            </div>
            <div>
              <Label className="text-xs">Subtasks run</Label>
              <Select value={form.execution_mode} onValueChange={(v) => set("execution_mode", v)}>
                <SelectTrigger data-testid="task-exec-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="parallel">Simultaneously</SelectItem>
                  <SelectItem value="sequential">In priority order</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.title.trim()}
            data-testid="task-save-btn">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
